import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { AdminPanelPage } from '../../AdminPanelPage';
import { ApplicantGroupForm } from './ApplicantGroupForm';
import { usePost } from '../../../API';
import { FilterGroupToBooleanExprConverter } from '../../shared/utils/filterConverters';
import { BooleanExpr } from '@aidkitorg/types/lib/survey';
import { ApplicantGroupFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';

export const CreateApplicantGroupPage: React.FC = () => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const createApplicantGroupApi = usePost('/api/admin_panel/applicant_groups/create');

  // Memoize editable props to prevent recreating objects on every render
  const editableTitleProps = React.useMemo(() => ({
    value: name,
    onChange: setName,
    label: strings.group_name,
    placeholder: strings.group_name_placeholder,
    error: {
      hasError: showValidation && !name.trim(),
      errorMessage: strings.error_message_title
    }
  }), [name, strings.group_name, strings.group_name_placeholder, showValidation, strings.error_message_title]);

  const editableDescriptionProps = React.useMemo(() => ({
    value: description,
    onChange: setDescription,
    label: strings.group_description,
    placeholder: strings.group_description_placeholder
  }), [description, strings.group_description, strings.group_description_placeholder]);

  const createApplicantGroup = async (data: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  }): Promise<{ id: string }> => {
    // Convert the filter config to BooleanExpr format
    const filterBooleanExpr: BooleanExpr | undefined = FilterGroupToBooleanExprConverter.convertFromConfig(data.filters);

    // Validate that we have a valid filter expression
    if (!filterBooleanExpr) {
      throw new Error('Failed to convert filters to valid expression');
    }

    // Call the API to create the applicant group
    const response = await createApplicantGroupApi({
      name: data.name,
      description: data.description || null,
      filter: filterBooleanExpr
    });

    if (response && typeof response === 'object' && 'data' in response &&
        typeof response.data === 'object' && response.data && 'id' in response.data &&
        typeof response.data.id === 'string') {
      return { id: response.data.id };
    }

    // If the response doesn't have an ID, throw an error
    throw new Error('Failed to create applicant group: Invalid response from server');
  };

  const handleSubmit = async (data: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  }) => {
    setIsSubmitting(true);

    try {
      const result = await createApplicantGroup(data);
      history.push(`/admin-panel/applicants/applicant-groups/${result.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      history.push('/admin-panel/applicants/applicant-groups');
    }
  };

  const pageActionButtons = [
    <Button
      key="cancel"
      type="button"
      variant="secondary"
      disabled={isSubmitting}
      onClick={handleCancel}
    >
      {strings.cancel}
    </Button>,
    <Button
      key="create"
      type="submit"
      variant="primary"
      disabled={isSubmitting}
      loading={isSubmitting}
      form="applicant-group-form"
    >
      {strings.create_group}
    </Button>
  ];

  return (
    <AdminPanelPage
      title={name}
      description={description}
      editableTitle={editableTitleProps}
      editableDescription={editableDescriptionProps}
      breadcrumbs={[
        { label: L.admin_panel.nav.applicants, href: '/admin-panel/applicants' },
        { label: strings.title, href: '/admin-panel/applicants/applicant-groups' },
        { label: strings.create_group }
      ]}
      pageActionButtons={pageActionButtons}
    >
      <ApplicantGroupForm
        mode="create"
        onSubmit={async (data) => {
          // Merge the name/description from state with the form data
          await handleSubmit({...data, name, description});
        }}
        isSubmitting={isSubmitting}
        name={name}
        description={description}
        showValidation={showValidation}
        setShowValidation={setShowValidation}
      />
    </AdminPanelPage>
  );

};
